/* eslint-disable class-methods-use-this */
import { dateFormatSwitcher, formatToIsoDate } from '@packages/toolbox/dist/utils/datetime';
import { AxiosPromise, AxiosResponse } from 'axios';
import { http } from 'SharedDir/backend/axios/http';
import SimpleApi from 'SharedDir/backend/api/classes/simpleApi';
import { BASE_PROJECTS_URL, PROJECT_ENDPOINTS } from 'SharedDir/backend/endpoints/projects';
import { IEndpointData, StatusResponse } from 'SharedDir/declarations/endpoint';
import { DocumentInterface, FolderCore } from 'SharedDir/declarations/filesystem';
import { Paginated, PaginationParams } from 'SharedDir/declarations/pagination';
import {
  Empty,
  ProjectFolder,
  TaskBare,
  TaskCommentCore,
  TaskCommentDocumentBare,
  TaskCommentDocumentCore,
  TaskCommentFull,
  TaskCore,
  TaskFull,
  TaskStatus,
} from 'SharedDir/declarations/project-tasks';
import {
  Project,
  ProjectFull,
  Responsible,
  RoadMapStepBare,
  RoadMapStepCreate,
} from 'SharedDir/declarations/projects';
import { Core, New } from 'SharedDir/declarations/shared';
import { UserBrief } from 'SharedDir/declarations/user';

export type Filters = {
  userProfileId: null | number | undefined;
  typeId: null | number | undefined;
  name: null | string | undefined;
  investmentCapacityFrom: null | number | undefined;
  investmentCapacityTo: null | number | undefined;
  startedYearFrom: null | string | Date | undefined;
  startedYearTo: null | string | Date | undefined;
  curatorId: null | number | undefined;
  showDeleted: boolean | undefined;
  municipalAreaId: null | number | undefined;
};

export default class ProjectApiClass extends SimpleApi<
  Project,
  New<Project>,
  Core<Project>,
  ProjectFull,
  Project,
  ProjectFull
> {
  constructor() {
    super(BASE_PROJECTS_URL);
  }

  /**
   * Получаем все проекты для профиля
   *
   * @static
   * @param {number} userProfileId
   * @param {string} [sortBy='name']
   * @returns {Promise<Array<Required<Project>>>}
   */
  async getAllForProfile(
    userProfileId: number,
    sortBy: string = 'name'
  ): Promise<Array<Required<Project>>> {
    return this.getAll({
      userProfileId,
      sortBy,
      rowsPerPage: null,
      descending: false,
      page: 1,
    });
  }

  /**
   * Удаляем проект профиля
   *
   * @static
   * @param {number} id
   * @returns {Promise<Project>}
   */
  async restore(id: string): Promise<Project> {
    const { data } = await (http({ ...PROJECT_ENDPOINTS.RESTORE(id) }) as AxiosPromise<Project>);
    return data;
  }

  /**
   * Редактируем контакты проекта
   *
   * @static
   * @param {number} id
   * @param {Array<Responsible>} object
   * @returns {Promise<StatusResponse>}
   */
  async setResponsible(id: number, object: Array<Responsible>): Promise<StatusResponse> {
    const { data } = await (http({
      ...PROJECT_ENDPOINTS.SET_RESPONSIBLE(id.toString()),
      data: { data: [...object] },
    }) as AxiosPromise<StatusResponse>);
    return data;
  }

  async fill(id: string, sectionId: number, newData): Promise<ProjectFull> {
    const { data } = await (http({
      ...PROJECT_ENDPOINTS.FILL(id, sectionId),
      data: newData,
    }) as AxiosPromise<ProjectFull>);
    return data;
  }

  async getRoadMapSpecific(projectId: string, roadStepId: string): Promise<RoadMapStepBare> {
    const { data } = await http({ ...PROJECT_ENDPOINTS.GET_ROADMAP_STEP(projectId, roadStepId) });
    return data;
  }

  /**
   * Перевод даты для отправки на бекенд
   * @param object
   * @private
   */
  private convertRoadMapDate<
    T extends RoadMapStepCreate | RoadMapStepBare | New<RoadMapStepCreate>
  >(object: T): T {
    const params = {
      ...object,
    };

    if (params.startedAt instanceof Date) {
      params.startedAt = formatToIsoDate(params.startedAt);
    }
    if (params.startedAt?.split('.').length !== 1) {
      params.startedAt = formatToIsoDate(
        dateFormatSwitcher(params.startedAt as string, 'DD.MM.YYYY')
      );
    }

    if (params.endedAt instanceof Date) {
      params.endedAt = formatToIsoDate(params.endedAt);
    }
    if (params.endedAt?.split('.').length !== 1) {
      params.endedAt = formatToIsoDate(dateFormatSwitcher(params.endedAt as string, 'DD.MM.YYYY'));
    }

    return params;
  }

  async createRoadMapStep(
    projectId: string,
    newObject: New<RoadMapStepCreate>
  ): Promise<RoadMapStepBare> {
    const params = this.convertRoadMapDate(newObject);
    const { data } = await (http({
      ...PROJECT_ENDPOINTS.CREATE_ROADMAP_STEP(projectId),
      data: params,
    }) as AxiosPromise<RoadMapStepBare>);
    return data;
  }

  async addRoadMapStep(
    projectId: string,
    newObject: New<RoadMapStepCreate>
  ): Promise<RoadMapStepBare> {
    const params = this.convertRoadMapDate(newObject);
    const { data } = await (http({
      ...PROJECT_ENDPOINTS.ADD_ROADMAP_STEP(projectId),
      data: params,
    }) as AxiosPromise<RoadMapStepBare>);
    return data;
  }

  async updateRoadMapStep(
    projectId: string,
    roadStepId: number,
    newObject: RoadMapStepCreate | RoadMapStepBare
  ): Promise<RoadMapStepBare> {
    const params = this.convertRoadMapDate(newObject);
    const { data } = await (http({
      ...PROJECT_ENDPOINTS.UPDATE_ROADMAP_STEP(projectId, roadStepId.toString()),
      data: params,
    }) as AxiosPromise<RoadMapStepBare>);
    return data;
  }

  async getRoadMapPaginated<T extends Empty = Empty>(
    projectId: string,
    params: Partial<PaginationParams<T>>
  ): Promise<Paginated<RoadMapStepBare, T>> {
    const { data } = await http({ ...PROJECT_ENDPOINTS.GET_ROADMAP_STEPS(projectId), params });
    return data;
  }

  async getRoadMapAll(projectId: string): Promise<Array<RoadMapStepBare>> {
    const params = {
      isApproved: true,
      page: 1,
      rowsPerPage: null,
    };
    const { data } = await this.getRoadMapPaginated(projectId, params);
    return data;
  }

  async doneRoadStep(
    projectId: string,
    roadStepId: number,
    flag: boolean,
    doneAt: string | null | Date
  ): Promise<StatusResponse> {
    const { data }: AxiosResponse<StatusResponse> = await http({
      ...PROJECT_ENDPOINTS.DONE(projectId, roadStepId.toString()),
      data: { flag, timestamp: doneAt },
    });

    return data;
  }

  async approveRoadStep(
    projectId: string,
    roadStepId: number,
    flag: boolean
  ): Promise<StatusResponse> {
    const { data }: AxiosResponse<StatusResponse> = await http({
      ...PROJECT_ENDPOINTS.APPROVE(projectId, roadStepId.toString()),
      data: { flag },
    });

    return data;
  }

  async deleteRoadStep(projectId: string, roadStepId: number) {
    const { data } = (await http(
      PROJECT_ENDPOINTS.DELETE_ROAD_STEP(projectId, roadStepId.toString())
    )) as AxiosResponse<StatusResponse>;
    return data;
  }

  /**
   * Получаем все статусы задач в проектах
   * @returns {Promise<Array<TaskStatus>>}
   */
  async getTaskStatuses() {
    const {
      data: { data },
    } = await (http({
      ...PROJECT_ENDPOINTS.TASK_STATUSES,
      params: {
        page: 1,
        rowsPerPage: 0,
      },
    }) as AxiosPromise<Paginated<TaskStatus>>);
    return data;
  }

  async createTask(projectId: string, task: TaskCore): Promise<TaskFull> {
    const config = { ...PROJECT_ENDPOINTS.CREATE_TASK(projectId), data: task };
    const { data } = (await http(config)) as AxiosResponse<TaskFull>;
    return data;
  }

  async updateTask(projectId: string, taskId: number, task: TaskCore): Promise<TaskFull> {
    const config = { ...PROJECT_ENDPOINTS.UPDATE_TASK(projectId, taskId.toString()), data: task };
    const { data } = (await http(config)) as AxiosResponse<TaskFull>;
    return data;
  }

  async updateTaskStatus(
    projectId: string,
    taskId: number,
    status: { statusId: number }
  ): Promise<TaskFull> {
    const config = {
      ...PROJECT_ENDPOINTS.UPDATE_TASK_STATUS(projectId, taskId.toString()),
      data: status,
    };
    const { data } = (await http(config)) as AxiosResponse<TaskFull>;
    return data;
  }

  async getTasksAll(id: string): Promise<Array<TaskBare>> {
    const {
      data: { data },
    } = (await http({
      ...PROJECT_ENDPOINTS.GET_TASKS(id),
      params: {
        rowsPerPage: 0,
        page: 1,
      },
    })) as AxiosResponse<Paginated<TaskBare>>;
    return data;
  }

  async getTask(projectId: string, taskId: number) {
    const { data } = (await http(
      PROJECT_ENDPOINTS.GET_TASK(projectId, taskId.toString())
    )) as AxiosResponse<TaskFull>;
    return data;
  }

  async saveTaskComment(projectId: string, taskId: number, comment: TaskCommentCore) {
    const { data } = (await http({
      ...PROJECT_ENDPOINTS.SAVE_TASK_COMMENT(projectId, taskId.toString()),
      data: comment,
    })) as AxiosResponse<TaskCommentFull>;
    return data;
  }

  async getTaskComments(projectId: string, taskId: number) {
    const {
      data: { data },
    } = (await http({
      ...PROJECT_ENDPOINTS.GET_TASK_COMMENTS(projectId, taskId),
      params: {
        rowsPerPage: 0,
        page: 1,
        descending: false,
        sortBy: 'created_at',
      },
    })) as AxiosResponse<Paginated<TaskCommentFull>>;
    return data;
  }

  async deleteTask(projectId: string, taskId: number) {
    const { data } = (await http(
      PROJECT_ENDPOINTS.DELETE_TASK(projectId, taskId.toString())
    )) as AxiosResponse<StatusResponse>;
    return data;
  }

  async getTaskWithComments(projectId: string, taskId: number) {
    const [task, comments] = await Promise.all([
      this.getTask(projectId, taskId),
      this.getTaskComments(projectId, taskId),
    ]);
    return { ...task, comments };
  }

  async toggleTaskWatch(projectId: string, taskId: string, status: boolean) {
    let endpoint: (...args: Array<string>) => IEndpointData;
    if (status) {
      endpoint = PROJECT_ENDPOINTS.WATCH_TASK;
    } else {
      endpoint = PROJECT_ENDPOINTS.UNWATCH_TASK;
    }
    const { data } = (await http(
      endpoint(projectId, taskId.toString())
    )) as AxiosResponse<TaskFull>;
    return data;
  }

  async getFolders(projectId: string, parentId?: number | null) {
    console.log(parentId);
    const {
      data: { data },
    } = (await http({
      ...PROJECT_ENDPOINTS.GET_FOLDERS(projectId),
      params: {
        page: 1,
        rowsPerPage: 0,
        parentId,
      },
    })) as AxiosResponse<Paginated<ProjectFolder>>;
    return data;
  }

  async createFolder(projectId: string, data: FolderCore<number>) {
    const { data: folder } = (await http({
      ...PROJECT_ENDPOINTS.CREATE_FOLDER(projectId),
      data,
    })) as AxiosResponse<ProjectFolder>;
    return folder;
  }

  async updateFolder(projectId: string, folderId: number, data: FolderCore<number>) {
    const { data: folder } = (await http({
      ...PROJECT_ENDPOINTS.UPDATE_FOLDER(projectId, folderId.toString()),
      data,
    })) as AxiosResponse<ProjectFolder>;
    return folder;
  }

  async deleteFolder(projectId: string, folderId: number) {
    const { data } = (await http(
      PROJECT_ENDPOINTS.DELETE_FOLDER(projectId, folderId.toString())
    )) as AxiosResponse<ProjectFolder>;
    return data;
  }

  async getFolder(projectId: string, folderId: number) {
    const { data } = (await http(
      PROJECT_ENDPOINTS.GET_FOLDER(projectId, folderId.toString())
    )) as AxiosResponse<ProjectFolder>;
    return data;
  }

  async createDocument(projectId: string, data: TaskCommentDocumentCore) {
    const { data: document } = await http({
      ...PROJECT_ENDPOINTS.CREATE_DOCUMENT(projectId),
      data,
    });
    return document;
  }

  async removeDocument(projectId: string, documentId: string) {
    const { data } = (await http(
      PROJECT_ENDPOINTS.DELETE_DOCUMENT(projectId, documentId)
    )) as AxiosResponse<TaskCommentDocumentBare>;
    return data;
  }

  async updateDocument(
    projectId: string,
    documentId: string,
    data: Omit<TaskCommentDocumentCore, 'attachmentId'>
  ) {
    const { data: document } = (await http({
      ...PROJECT_ENDPOINTS.UPDATE_DOCUMENT(projectId, documentId),
      data,
    })) as AxiosResponse<TaskCommentDocumentBare>;
    return document;
  }

  /**
   * Скачать отчёт по всем проектам
   * Формат: Реестр инвестиционных проектов
   *
   * @returns
   */
  async downloadRegistryReport(): Promise<AxiosResponse> {
    return http({
      ...PROJECT_ENDPOINTS.REPORTS.REGISTRY,
      responseType: 'blob',
    })
      .then((r): AxiosResponse => r)
      .catch((e) => {
        throw e;
      });
  }

  /**
   * Скачать отчёт по всем проектам
   * Формат: Отчетность по текущим проектам
   *
   * @returns
   */
  async downloadRegistryFondugraReport(): Promise<AxiosResponse> {
    return http({
      ...PROJECT_ENDPOINTS.REPORTS.REGISTRY_FOND,
      responseType: 'blob',
    })
      .then((r): AxiosResponse => r)
      .catch((e) => {
        throw e;
      });
  }

  /**
   * Скачать отчёт по всем проектам
   * Формат: Мониторинг ГЧП проектов (концесионные)
   *
   * @param eventId
   * @returns
   */
  // async downloadRegistryPartnershipMonitoringGcp(): Promise<AxiosResponse> {
  //   const responseData: AxiosResponse = await http({
  //     ...PROJECT_ENDPOINTS.REPORTS.PARTNERSHIP_MONITORING_GCP,
  //     params: {
  //       projectTypeId: PROJECT_TYPE_GCHP_ID,
  //     },
  //     responseType: 'blob',
  //   })
  //     .then((r): AxiosResponse => r)
  //     .catch((e) => {
  //       throw e;
  //     });
  //
  //   return responseData;
  // }

  /**
   * Скачать Паспорт проекта
   * Формат: Паспорт проекта
   *
   * @returns
   * @param projectId
   */
  async downloadProjectPassport(projectId: number): Promise<AxiosResponse> {
    return http({
      ...PROJECT_ENDPOINTS.REPORTS.PROJECT_PASSPORT(projectId),
      responseType: 'blob',
    })
      .then((r): AxiosResponse => r)
      .catch((e) => {
        throw e;
      });
  }

  /**
   * Скачать таблицу Мониторинга проекта (KPI)
   * Формат: Таблица мониторинга проекта (KPI)
   *
   * @returns
   * @param projectId
   */
  async downloadProjectMonitoring(projectId: number): Promise<AxiosResponse> {
    return http({
      ...PROJECT_ENDPOINTS.REPORTS.PROJECT_PASSPORT_KPI(projectId),
      responseType: 'blob',
    })
      .then((r): AxiosResponse => r)
      .catch((e) => {
        throw e;
      });
  }

  /**
   * Скачать дорожную карту по проекту
   * Формат: Свод по выполнению дорожной карты
   *
   * @returns
   * @param projectId
   */
  async downloadProjectRoadmapReport(projectId: number): Promise<AxiosResponse> {
    return http({
      ...PROJECT_ENDPOINTS.REPORTS.PROJECT_ROADMAP(projectId),
      responseType: 'blob',
    })
      .then((r): AxiosResponse => r)
      .catch((e) => {
        throw e;
      });
  }

  /**
   * Скачать услуги проекта
   * Формат: Свод по услугам проекта
   *
   * @returns
   * @param projectId
   */
  async downloadProjectIssues(projectId: number): Promise<AxiosResponse> {
    return http({
      ...PROJECT_ENDPOINTS.REPORTS.PROJECT_ISSUES(projectId),
      responseType: 'blob',
    })
      .then((r): AxiosResponse => r)
      .catch((e) => {
        throw e;
      });
  }

  async joinChat(projectId: number): Promise<StatusResponse> {
    const { data } = await http(PROJECT_ENDPOINTS.JOIN_CHAT(projectId.toString()));
    return data;
  }

  async getMembers(projectId: number): Promise<Array<UserBrief>> {
    const {
      data: { data },
    } = (await http({
      ...PROJECT_ENDPOINTS.GET_MEMBERS(projectId.toString()),
      params: {
        page: 1,
        rowsPerPage: 0,
      },
    })) as AxiosResponse<Paginated<UserBrief>>;
    return data;
  }
}
